import React, { useCallback, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import { useDragLayer, useDrop } from "react-dnd";
import { v4 as uuidv4 } from "uuid";

import DropzoneList from "./DropzoneList";
import { DRAG_TYPE } from "../../constants/dragTypes";
import ThreeFilesIcon from "../../resources/icons/ThreeFilesIcon";
import Spinner from "../utils/Spinner";

import type { Identifier } from "dnd-core";
import type { ComponentType, JSX } from "react";
import type { ConnectDragSource } from "react-dnd";

import type { DropzoneMoveItemParams } from "../../types/fast";

export type DropzoneRenderItem<T> = ComponentType<DropzoneRenderItemProps<T>>;

export interface DropzoneRenderItemProps<T> {
  item: T & DropzoneItem;
  isDragging: boolean;
  isDragDisabled: boolean;
  onDeleteClick: (uuid: string) => void;
  dragRef: ConnectDragSource;
}

interface CollectedDragProps {
  isOver: boolean;
  canDrop: boolean;
  dropzoneContainerId: Identifier | null;
}

export interface InitialDropzoneItem {
  uuid: string;
  rank: number;
}
export interface DropzoneItem {
  isTemp?: boolean;
  dropzoneContainerId: Identifier | null;
}

interface DropzoneListContainerProps<T extends InitialDropzoneItem> {
  acceptList?: string[];
  renderFormatedAcceptedList?: JSX.Element;
  sizeLimit?: number;
  isDragDisabled?: boolean;
  initialItemList?: T[];
  onChange?: (items: T[]) => void;
  placeholder?: {
    text: string;
    withIcon?: boolean;
  };
  isLoading?: boolean;
  renderItem: DropzoneRenderItem<T>;
  classNamesContainer?: string;
  isFullSize?: boolean;
}

function DropzoneListContainer<T extends InitialDropzoneItem>({
  acceptList = [],
  renderFormatedAcceptedList,
  initialItemList,
  sizeLimit = -1,
  isDragDisabled,
  onChange,
  placeholder,
  isLoading,
  renderItem,
  classNamesContainer,
  isFullSize,
}: DropzoneListContainerProps<T>): JSX.Element {
  const [itemList, setItemList] = useState<(T & DropzoneItem)[]>([]);
  const [tempItemList, setTempItemList] = useState<(T & DropzoneItem)[]>([]);

  const itemListRef = useRef<(T & DropzoneItem)[]>([]);
  itemListRef.current = itemList;
  const tempItemListRef = useRef<(T & DropzoneItem)[]>([]);
  tempItemListRef.current = tempItemList;

  const { isDragging } = useDragLayer((monitor) => ({
    isDragging:
      monitor.isDragging() &&
      typeof monitor.getItemType() === "string" &&
      (monitor.getItemType() as string).includes(DRAG_TYPE.MEDIA_LIBRARY_ITEM),
  }));

  const limitReached = sizeLimit >= 0 && itemList.length >= sizeLimit;

  const handleItemDrop = useCallback(
    (newItem: T): void => {
      const currentList = itemListRef.current;
      tempItemListRef.current = [];
      setTempItemList([]);

      const prevItemList = [...currentList];
      const updatedNewItem = {
        ...newItem,
        uuid: uuidv4(),
        rank: newItem.rank ?? currentList.length,
      };
      const updatedList = prevItemList.filter((item) => !item.isTemp);

      onChange?.([...updatedList, updatedNewItem]);
    },
    [onChange],
  );

  const dropRef = useRef<HTMLDivElement>(null);

  const [{ canDrop, isOver, dropzoneContainerId }, dropConnector] = useDrop<
    T,
    unknown,
    CollectedDragProps
  >(
    () => ({
      accept: acceptList,
      drop: (item): void => handleItemDrop(item),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        dropzoneContainerId: monitor.getHandlerId(),
      }),
    }),
    [acceptList, itemList],
  );

  dropConnector(dropRef);

  const saveTempItemList = useCallback((list: (T & DropzoneItem)[]): void => {
    if (!tempItemListRef.current.length) {
      tempItemListRef.current = list;
      setTempItemList(list);
    }
  }, []);

  const resetTempMediaList = useCallback((): void => {
    if (tempItemListRef.current.length === 0) return;
    setItemList(tempItemListRef.current);
    onChange?.(tempItemListRef.current);
    setTempItemList([]);
  }, [onChange]);

  useEffect(() => {
    if (!initialItemList) return;

    const updatedItemList = initialItemList.map((item) => ({
      ...item,
      dropzoneContainerId,
    }));

    setItemList(updatedItemList);
  }, [initialItemList, dropzoneContainerId]);

  useEffect(() => {
    if (!isDragging && !isOver) {
      resetTempMediaList();
    }
  }, [isOver, isDragging, resetTempMediaList]);

  const handleDeleteClick = (uuid: string): void => {
    const deletedItem = itemList.find((item) => item.uuid === uuid);

    if (!deletedItem) return;

    const deletedRank = deletedItem.rank;

    setItemList((prevItemList) => {
      const updatedItemList = prevItemList
        .filter((item) => item.uuid !== uuid)
        .map((item) => {
          if (item.rank > deletedRank) {
            return {
              ...item,
              rank: item.rank - 1,
            };
          }

          return item;
        });

      onChange?.(updatedItemList);

      return updatedItemList;
    });
  };

  const handleMoveItem = useCallback(
    ({ itemUuid, newRank, newItem }: DropzoneMoveItemParams): void => {
      const prevItemList = [...itemListRef.current];

      const oldRank = prevItemList.find((item) => item.uuid === itemUuid)?.rank;

      if (oldRank === undefined) {
        saveTempItemList(prevItemList);
      }

      const updatedList = prevItemList.map((item) => {
        // Add new media from library
        if (oldRank === undefined) {
          const isAfterNewIndex = item.rank >= newRank;

          if (isAfterNewIndex) {
            return {
              ...item,
              rank: item.rank + 1,
            };
          }

          return item;
        }

        // move all items between old and new index
        const isAfterOldAndBeforeNewIndex =
          item.rank > oldRank && item.rank <= newRank;
        const isAfterNewAndBeforeOldIndex =
          item.rank < oldRank && item.rank >= newRank;

        if (isAfterOldAndBeforeNewIndex) {
          return {
            ...item,
            rank: item.rank - 1,
          };
        }

        if (isAfterNewAndBeforeOldIndex) {
          return {
            ...item,
            rank: item.rank + 1,
          };
        }

        // move the item from old to new index
        if (item.rank === oldRank) {
          return {
            ...item,
            rank: newRank,
          };
        }

        return item;
      });

      if (oldRank === undefined && newItem) {
        updatedList.push({
          ...newItem,
          rank: newRank,
          isTemp: true,
          dropzoneContainerId,
        } as T & DropzoneItem);
      }

      itemListRef.current = updatedList;
      setItemList(updatedList);
      onChange?.(updatedList);
    },
    [onChange, saveTempItemList, dropzoneContainerId],
  );

  const dropzoneClassNames = classNames(
    "flex size-full flex-col items-center rounded-[0.46875rem] bg-muted border-2 border-dashed",
    isDragging && !limitReached && canDrop && "border-primary",
    isDragging && (limitReached || !canDrop) && "border-destructive",
    !isDragging && "border-transparent",
    isOver && canDrop && "bg-primary/10",
    classNamesContainer,
  );

  const renderDropzoneContent = (): JSX.Element | string | null => {
    if (isLoading) {
      return <Spinner />;
    }

    if (itemList.length > 0) {
      return (
        <DropzoneList
          acceptList={acceptList}
          isDragDisabled={isDragDisabled ?? limitReached}
          isDropDisabled={!canDrop || limitReached}
          isFullSize={isFullSize}
          itemList={itemList}
          onDeleteClick={handleDeleteClick}
          onMoveItem={handleMoveItem}
          renderItem={renderItem}
        />
      );
    }

    if (placeholder?.withIcon) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-[0.46875rem] py-[1.5625rem]">
          <ThreeFilesIcon />
          <div>{placeholder?.text}</div>
        </div>
      );
    }

    return placeholder?.text ?? renderFormatedAcceptedList ?? null;
  };

  return (
    <div ref={limitReached ? null : dropRef} className={dropzoneClassNames}>
      {renderDropzoneContent()}
    </div>
  );
}

export default DropzoneListContainer;
